import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, signal } from '@angular/core';
import { NgOptimizedImage, UpperCasePipe } from '@angular/common';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { TranslocoDirective } from '@jsverse/transloco';
import { LanguageService } from '../../core/services/language.service';
import { environment } from '../../../environments/environment';
import { ancHome, ancPath } from './anc-links';
import { AncStatusService } from './anc-status.service';
import { Universe, UniverseKey, UniverseLink, injectUniverses } from './anc-universes';

/**
 * Full-width navigation of the October 2026 art direction: announcement bar,
 * utility bar (status, phone, email, support, language), sticky bar and one mega-menu per universe. Menus open on hover and keyboard
 * focus through CSS; the triggers also toggle them for touch and keyboard users.
 */
@Component({
  selector: 'app-anc-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, NgOptimizedImage, UpperCasePipe, TranslocoDirective],
  templateUrl: './anc-header.component.html',
  styleUrl: './anc-header.component.css',
  host: {
    '(document:keydown.escape)': 'closeAll()',
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class AncHeaderComponent {
  private readonly language = inject(LanguageService);
  private readonly router = inject(Router);
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly statusService = inject(AncStatusService);

  protected readonly universes = injectUniverses();
  protected readonly panelUrl = environment.panelUrl;
  protected readonly openKey = signal<UniverseKey | null>(null);
  protected readonly mobileOpen = signal(false);
  /**
   * After a link is followed, hover and focus would keep the mega-menu open
   * under the pointer: it stays closed until the pointer leaves the menu.
   */
  protected readonly locked = signal(false);

  /** Shown once the public status API has answered; the server render leaves it out. */
  protected readonly serviceState = computed(() => {
    const operational = this.statusService.operational();
    if (operational === null) return null;
    return operational ? 'operational' : 'degraded';
  });

  private readonly lang = computed(() => this.language.currentLang());
  protected readonly currentLang = this.lang;
  protected readonly languages = this.language.languages;
  protected readonly langOpen = signal(false);

  protected languagePath(code: string): string {
    return this.language.pathForLang(code, this.currentUrl());
  }

  protected href(link: UniverseLink): string {
    return ancPath(this.lang(), link.path);
  }

  protected home(): string {
    return ancHome(this.lang());
  }

  protected toggle(universe: Universe): void {
    this.locked.set(false);
    this.openKey.update((key) => (key === universe.key ? null : universe.key));
  }

  protected toggleMobile(): void {
    this.mobileOpen.update((open) => !open);
  }

  protected closeAll(): void {
    this.openKey.set(null);
    this.langOpen.set(false);
    this.mobileOpen.set(false);
    this.locked.set(true);
    const focused = this.host.nativeElement.ownerDocument.activeElement;
    if (focused instanceof HTMLElement && this.host.nativeElement.contains(focused)) focused.blur();
  }

  /** A click outside the navigation closes a menu opened from its trigger. */
  protected onDocumentClick(event: MouseEvent): void {
    if (this.langOpen() && !(event.target as Element | null)?.closest('.lang')) this.langOpen.set(false);
    if (this.openKey() && !this.host.nativeElement.contains(event.target as Node)) {
      this.openKey.set(null);
    }
  }
}
